import React, { useState, useEffect, useRef } from 'react';
import { X, ArrowLeft, Settings, Trash2 } from 'lucide-react';
import { useTeamStore } from '../../store/teamStore';
import { supabase } from '../../lib/supabase';
import VisualFormationBuilder, { type FormationPosition } from './VisualFormationBuilder';
import SampleFormations from './SampleFormations';
import PositionMappingEditor from './PositionMappingEditor';
import type { Sport } from '../../types';

type ViewMode = 'select' | 'builder' | 'mapping';

interface FormationBuilderManagerProps {
  unit: 'Offense' | 'Defense' | 'Special Teams';
  onClose: () => void;
  onFormationCreated?: (formationId: string) => void;
  editingFormationId?: string;
  initialPositions?: FormationPosition[];
  initialFormationName?: string;
}

const FormationBuilderManager: React.FC<FormationBuilderManagerProps> = ({
  unit,
  onClose,
  onFormationCreated,
  editingFormationId,
  initialPositions: propInitialPositions,
  initialFormationName: propInitialFormationName
}) => {
  const { currentTeam } = useTeamStore();
  // If editing or has initial data, start in builder mode, otherwise start in select mode
  const shouldStartInBuilder = !!(editingFormationId || (propInitialPositions && propInitialPositions.length > 0));
  const [viewMode, setViewMode] = useState<ViewMode>(shouldStartInBuilder ? 'builder' : 'select');
  const [currentPositions, setCurrentPositions] = useState<FormationPosition[]>(propInitialPositions || []);
  // Use a ref to track the latest positions to avoid stale state issues
  const latestPositionsRef = useRef<FormationPosition[]>(propInitialPositions || []);
  const [formationName, setFormationName] = useState(propInitialFormationName || '');
  const [formationDescription, setFormationDescription] = useState('');
  const [savedFormationId, setSavedFormationId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(!!editingFormationId); // Start loading if editing by ID
  const [originalFormationName, setOriginalFormationName] = useState<string | null>(propInitialFormationName || null); // Track if this came from a predefined formation
  const [validationError, setValidationError] = useState<string | null>(null); // Form validation error

  // Keep ref in sync with state
  useEffect(() => {
    latestPositionsRef.current = currentPositions;
  }, [currentPositions]);

  // Load existing formation if editing by ID
  useEffect(() => {
    if (editingFormationId) {
      loadFormation(editingFormationId);
    }
  }, [editingFormationId]);

  // If initial positions/name provided, set them up
  useEffect(() => {
    if (propInitialPositions && propInitialPositions.length > 0 && !editingFormationId) {
      setCurrentPositions(propInitialPositions);
      latestPositionsRef.current = propInitialPositions; // Update ref when setting initial positions
      if (propInitialFormationName) {
        setFormationName(propInitialFormationName);
        setOriginalFormationName(propInitialFormationName);
      }
      setViewMode('builder');
    }
  }, [propInitialPositions, propInitialFormationName, editingFormationId]);

  const loadFormation = async (formationId: string) => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('custom_formations')
        .select('*')
        .eq('id', formationId)
        .single();

      if (error) throw error;

      setFormationName(data.name);
      setFormationDescription(data.description || '');
      setSavedFormationId(formationId);

      // Convert stored positions to FormationPosition format
      console.log('[FormationBuilderManager] Loading formation positions from DB:', JSON.stringify(data.positions, null, 2));
      const positions: FormationPosition[] = (data.positions || []).map((pos: any, index: number) => {
        const label = (pos.name || pos.label || '').toUpperCase(); // Always uppercase
        // Prioritize position_group_key over positionGroupKey (since we save both, but position_group_key is more reliable)
        const positionGroupKey = pos.position_group_key || pos.positionGroupKey;
        console.log(`[FormationBuilderManager] Loading position ${label}:`, { 
          raw_positionGroupKey: pos.positionGroupKey, 
          raw_position_group_key: pos.position_group_key,
          final_positionGroupKey: positionGroupKey,
          full_pos: pos
        });
        return {
          id: pos.id || `pos_${index}`,
          label,
          fullName: pos.fullName || pos.full_name || '',
          x: pos.x || pos.x_coordinate || 50,
          y: pos.y || pos.y_coordinate || 50,
          positionGroupKey: positionGroupKey || undefined, // Use undefined instead of empty string
          color: pos.color || '#1E40AF',
          isCenter: pos.isCenter !== undefined ? pos.isCenter : label === 'C',
        };
      });
      
      // If Football Offense and no OL positions exist, add them
      if (currentTeam.sport === 'Football' && unit === 'Offense') {
        const hasOL = positions.some(p => p.positionGroupKey === 'OL');
        if (!hasOL) {
          const defaultOL = [
            { id: 'ol_lt', label: 'LT', fullName: 'Left Tackle', x: 35, y: 50, positionGroupKey: 'OL', color: '#1E40AF', isCenter: false },
            { id: 'ol_lg', label: 'LG', fullName: 'Left Guard', x: 42.5, y: 50, positionGroupKey: 'OL', color: '#1E40AF', isCenter: false },
            { id: 'ol_c', label: 'C', fullName: 'Center', x: 50, y: 50, positionGroupKey: 'OL', color: '#1E40AF', isCenter: true },
            { id: 'ol_rg', label: 'RG', fullName: 'Right Guard', x: 57.5, y: 50, positionGroupKey: 'OL', color: '#1E40AF', isCenter: false },
            { id: 'ol_rt', label: 'RT', fullName: 'Right Tackle', x: 65, y: 50, positionGroupKey: 'OL', color: '#1E40AF', isCenter: false },
          ];
          positions.push(...defaultOL);
        }
      }

      setCurrentPositions(positions);
      latestPositionsRef.current = positions; // Update ref when loading
      setViewMode('builder');
    } catch (error) {
      console.error('Error loading formation:', error);
      alert('Failed to load formation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectSampleFormation = async (positions: FormationPosition[], formationName: string) => {
    if (!currentTeam) return;
    
    // Auto-save the formation with the predefined name and go directly to mappings
    setOriginalFormationName(formationName);
    setFormationName(formationName);
    setCurrentPositions(positions);
    
    // Immediately save and go to mappings
    await handleSaveFormationDirect(positions, formationName);
  };

  const handleSaveFormationDirect = async (positions: FormationPosition[], name: string) => {
    if (!currentTeam) return;

    try {
      setIsSaving(true);

      // Validate all positions have mappings
      const unmappedPositions = positions.filter(p => p.label.trim() && !p.positionGroupKey);
      if (unmappedPositions.length > 0) {
        // If positions are missing mappings, go to builder first
        setFormationName(name);
        setCurrentPositions(positions);
        setViewMode('builder');
        setIsSaving(false);
        return;
      }

      // Convert FormationPosition to storage format
      const positionsToSave = positions.map((pos) => ({
        id: pos.id,
        name: pos.label.toUpperCase(),
        label: pos.label.toUpperCase(),
        fullName: pos.fullName,
        full_name: pos.fullName,
        x: pos.x,
        x_coordinate: pos.x,
        y: pos.y,
        y_coordinate: pos.y,
        positionGroupKey: pos.positionGroupKey,
        position_group_key: pos.positionGroupKey,
        color: pos.color,
        isCenter: pos.isCenter || false,
      }));

      // Create new formation
      const { data, error } = await supabase
        .from('custom_formations')
        .insert({
          team_id: currentTeam.id,
          name: name,
          description: '',
          positions: positionsToSave,
          sport: currentTeam.sport,
          unit: unit,
        })
        .select()
        .single();

      if (error) throw error;
      const formationId = data.id;
      setSavedFormationId(formationId);

      // Track custom formation in team_formations
      try {
        // Remove any predefined formation entry with the same name
        await supabase
          .from('team_formations')
          .delete()
          .eq('team_id', currentTeam.id)
          .eq('formation_name', name)
          .eq('sport', currentTeam.sport)
          .eq('unit', unit)
          .eq('is_predefined', true);

        // Insert the custom formation entry
        const { error: trackError } = await supabase
          .from('team_formations')
          .insert({
            team_id: currentTeam.id,
            formation_name: name,
            is_predefined: false,
            custom_formation_id: formationId,
            sport: currentTeam.sport,
            unit: unit
          });

        if (trackError && trackError.code !== '23505') {
          console.error('Error tracking team formation:', trackError);
        }
      } catch (error) {
        console.error('Error tracking team formation:', error);
      }

      // Automatically save position mappings based on positionGroupKey
      // Since positions are already mapped when using "as-is", save them automatically
      if (formationId) {
        try {
          // Delete existing mappings for this formation
          await supabase
            .from('position_mappings')
            .delete()
            .eq('team_id', currentTeam.id)
            .eq('formation_id', formationId)
            .eq('sport', currentTeam.sport)
            .eq('unit', unit);

          // Create new mappings based on positionGroupKey from each position
          const mappingInserts: any[] = [];
          positions.forEach((pos) => {
            if (pos.label.trim() && pos.positionGroupKey) {
              mappingInserts.push({
                team_id: currentTeam.id,
                formation_id: formationId,
                formation_name: name,
                position_label: pos.label.toUpperCase(),
                position_group_key: pos.positionGroupKey,
                sport: currentTeam.sport,
                unit: unit,
              });
            }
          });

          if (mappingInserts.length > 0) {
            const { error: mappingError } = await supabase
              .from('position_mappings')
              .insert(mappingInserts);

            if (mappingError) {
              console.error('Error saving position mappings:', mappingError);
              // Don't throw - mappings are secondary to formation save
            } else {
              console.log('[FormationBuilderManager] Successfully saved position mappings for', mappingInserts.length, 'positions');
            }
          }
        } catch (error) {
          console.error('Error saving position mappings:', error);
          // Don't throw - mappings are secondary to formation save
        }
      }

      // Refresh formations
      if (onFormationCreated) {
        onFormationCreated(formationId);
      }

      // Since positions are already mapped when using "as-is", close the modal
      onClose();
    } catch (error) {
      console.error('Error saving formation:', error);
      // If save fails, go to builder so user can fix issues
      setFormationName(name);
      setCurrentPositions(positions);
      setViewMode('builder');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUseAsTemplate = async (positions: FormationPosition[], formationName: string) => {
    // When using as template, pre-fill the formation name with the predefined name
    setOriginalFormationName(formationName);
    setFormationName(formationName);
    setCurrentPositions(positions);
    setViewMode('builder');
  };

  const handleStartCustomFormation = () => {
    setCurrentPositions([]);
    setFormationName('');
    setFormationDescription('');
    setSavedFormationId(null);
    setOriginalFormationName(null);
    setViewMode('builder');
  };

  const handleSaveFormation = async () => {
    if (!currentTeam) return;
    
    // Clear previous validation errors
    setValidationError(null);
    
    // Validate formation name
    if (!formationName.trim()) {
      setValidationError('Please enter a formation name');
      return;
    }

    try {
      setIsSaving(true);

      // Use the ref to get the absolute latest positions (avoids stale state issues)
      const positionsToValidate = latestPositionsRef.current;
      console.log('[FormationBuilderManager] Saving formation with positions (from ref):', positionsToValidate.map(p => ({ id: p.id, label: p.label, positionGroupKey: p.positionGroupKey })));

      // Validate all positions have mappings
      const unmappedPositions = positionsToValidate.filter(p => p.label.trim() && !p.positionGroupKey);
      if (unmappedPositions.length > 0) {
        setValidationError(`Please map all positions to position groups. ${unmappedPositions.length} position(s) are missing mappings: ${unmappedPositions.map(p => p.label).join(', ')}`);
        setIsSaving(false);
        return;
      }

      // Convert FormationPosition to storage format
      const positionsToSave = positionsToValidate.map((pos) => {
        // Ensure positionGroupKey is saved correctly (don't use || null which would convert empty strings to null)
        const positionGroupKeyValue = pos.positionGroupKey || null;
        const saved = {
          id: pos.id,
          name: pos.label.toUpperCase(), // Always uppercase
          label: pos.label.toUpperCase(), // Always uppercase
          fullName: pos.fullName || null,
          full_name: pos.fullName || null,
          x: pos.x,
          x_coordinate: pos.x,
          y: pos.y,
          y_coordinate: pos.y,
          positionGroupKey: positionGroupKeyValue, // Save the actual value
          position_group_key: positionGroupKeyValue, // Save the actual value (both for compatibility)
          color: pos.color || '#1E40AF',
          isCenter: pos.isCenter || false,
        };
        console.log('[FormationBuilderManager] Saving position:', { 
          id: saved.id, 
          label: saved.label, 
          input_positionGroupKey: pos.positionGroupKey,
          saved_positionGroupKey: saved.positionGroupKey, 
          saved_position_group_key: saved.position_group_key 
        });
        return saved;
      });
      console.log('[FormationBuilderManager] Full positions array being saved:', JSON.stringify(positionsToSave, null, 2));

      let formationId = savedFormationId;

      if (formationId) {
        // Update existing formation by ID
        console.log('[FormationBuilderManager] Updating existing formation:', formationId);
        const { error } = await supabase
          .from('custom_formations')
          .update({
            name: formationName,
            description: formationDescription,
            positions: positionsToSave,
            updated_at: new Date().toISOString(),
          })
          .eq('id', formationId);

        if (error) {
          console.error('[FormationBuilderManager] Error updating formation:', error);
          throw error;
        }
        console.log('[FormationBuilderManager] Successfully updated formation:', formationId);
      } else {
        // Check if a custom formation with this name already exists
        const { data: existingFormation } = await supabase
          .from('custom_formations')
          .select('id')
          .eq('team_id', currentTeam.id)
          .eq('sport', currentTeam.sport)
          .eq('unit', unit)
          .eq('name', formationName)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (existingFormation?.id) {
          // Update existing formation instead of creating a new one
          console.log('[FormationBuilderManager] Found existing formation with same name, updating:', existingFormation.id);
          const { error } = await supabase
            .from('custom_formations')
            .update({
              name: formationName,
              description: formationDescription,
              positions: positionsToSave,
              updated_at: new Date().toISOString(),
            })
            .eq('id', existingFormation.id);

          if (error) {
            console.error('[FormationBuilderManager] Error updating existing formation:', error);
            throw error;
          }
          console.log('[FormationBuilderManager] Successfully updated existing formation:', existingFormation.id);
          formationId = existingFormation.id;
          setSavedFormationId(formationId);
        } else {
          // Create new formation
          console.log('[FormationBuilderManager] Creating new formation:', { name: formationName, team_id: currentTeam.id, sport: currentTeam.sport, unit });
          const { data, error } = await supabase
            .from('custom_formations')
            .insert({
              team_id: currentTeam.id,
              name: formationName,
              description: formationDescription,
              positions: positionsToSave,
              sport: currentTeam.sport,
              unit: unit,
            })
            .select()
            .single();

          if (error) {
            console.error('[FormationBuilderManager] Error creating formation:', error);
            throw error;
          }
          console.log('[FormationBuilderManager] Successfully created formation:', data.id);
          formationId = data.id;
          setSavedFormationId(formationId);
        }
      }

      // Automatically update position mappings based on positionGroupKey
      if (formationId) {
        try {
          // Delete existing mappings for this formation
          await supabase
            .from('position_mappings')
            .delete()
            .eq('team_id', currentTeam.id)
            .eq('formation_id', formationId)
            .eq('sport', currentTeam.sport)
            .eq('unit', unit);

          // Create new mappings based on positionGroupKey from each position
          const mappingInserts: any[] = [];
          positionsToValidate.forEach((pos) => {
            if (pos.label.trim() && pos.positionGroupKey) {
              mappingInserts.push({
                team_id: currentTeam.id,
                formation_id: formationId,
                formation_name: formationName,
                position_label: pos.label.toUpperCase(),
                position_group_key: pos.positionGroupKey,
                sport: currentTeam.sport,
                unit: unit,
              });
            }
          });

          if (mappingInserts.length > 0) {
            const { error: mappingError } = await supabase
              .from('position_mappings')
              .insert(mappingInserts);

            if (mappingError) {
              console.error('Error saving position mappings:', mappingError);
              // Don't throw - mappings are secondary to formation save
            } else {
              console.log('[FormationBuilderManager] Successfully updated position mappings for', mappingInserts.length, 'positions');
            }
          }
        } catch (error) {
          console.error('Error updating position mappings:', error);
          // Don't throw - mappings are secondary to formation save
        }
      }

      // Track custom formation in team_formations
      if (formationId) {
        try {
          // First, remove any predefined formation entry with the same name (if user kept the same name)
          await supabase
            .from('team_formations')
            .delete()
            .eq('team_id', currentTeam.id)
            .eq('formation_name', formationName)
            .eq('sport', currentTeam.sport)
            .eq('unit', unit)
            .eq('is_predefined', true);

          // If this was created from a predefined formation and the name changed, remove the original predefined entry
          if (originalFormationName && originalFormationName !== formationName) {
            await supabase
              .from('team_formations')
              .delete()
              .eq('team_id', currentTeam.id)
              .eq('formation_name', originalFormationName)
              .eq('sport', currentTeam.sport)
              .eq('unit', unit)
              .eq('is_predefined', true);
          }

          // Insert the custom formation entry
          const { error } = await supabase
            .from('team_formations')
            .insert({
              team_id: currentTeam.id,
              formation_name: formationName,
              is_predefined: false,
              custom_formation_id: formationId,
              sport: currentTeam.sport,
              unit: unit
            });

          // Ignore unique constraint errors (formation already tracked)
          if (error && error.code !== '23505') {
            console.error('Error tracking team formation:', error);
          }

          // Clear the original formation name after saving
          setOriginalFormationName(null);
        } catch (error) {
          console.error('Error tracking team formation:', error);
        }
      }

      // Refresh team formations after saving
      // Import and call fetchTeamFormations to ensure the formation appears in dropdown
      try {
        // Trigger a refresh by calling the callback which should refresh formations
        if (onFormationCreated) {
          onFormationCreated(formationId);
        }
      } catch (error) {
        console.error('Error in onFormationCreated callback:', error);
      }

      // Move to mapping view
      setViewMode('mapping');
      setValidationError(null); // Clear validation error on success
    } catch (error) {
      console.error('Error saving formation:', error);
      setValidationError('Failed to save formation. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    if (viewMode === 'mapping') {
      setViewMode('builder');
    } else if (viewMode === 'builder') {
      if (currentPositions.length === 0 && !formationName) {
        setViewMode('select');
      } else {
        // Ask for confirmation
        if (confirm('Are you sure you want to go back? Your changes will be saved.')) {
          handleSaveFormation();
          setViewMode('select');
        }
      }
    }
  };

  if (!currentTeam) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6">
          <p>No team selected</p>
          <button onClick={onClose} className="btn btn-primary mt-4">
            Close
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6">
          <div className="loading loading-spinner loading-lg"></div>
          <p className="mt-4">Loading formation...</p>
        </div>
      </div>
    );
  }

  const handleDeleteFormation = async () => {
    if (!savedFormationId || !currentTeam) return;
    
    if (!confirm('Are you sure you want to delete this formation? This action cannot be undone.')) {
      return;
    }

    try {
      // Delete from custom_formations (cascade will handle position_mappings and team_formations)
      const { error } = await supabase
        .from('custom_formations')
        .delete()
        .eq('id', savedFormationId)
        .eq('team_id', currentTeam.id);

      if (error) throw error;

      // Refresh formations
      if (onFormationCreated) {
        onFormationCreated(null);
      }

      // Close the modal
      onClose();
    } catch (error) {
      console.error('Error deleting formation:', error);
      setValidationError('Failed to delete formation. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 pb-20 md:pb-4" style={{ paddingBottom: '5rem' }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl flex flex-col" style={{ maxHeight: 'calc(100vh - 6rem)', height: 'calc(100vh - 6rem)' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
          <div className="flex items-center gap-4">
            {viewMode !== 'select' && (
              <button
                onClick={handleBack}
                className="btn btn-ghost btn-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
            )}
            <div>
              <h2 className="text-xl font-bold">
                {viewMode === 'select' && 'Create Formation'}
                {viewMode === 'builder' && (editingFormationId ? 'Edit Formation' : 'Build Formation')}
                {viewMode === 'mapping' && 'Position Mappings'}
              </h2>
              <p className="text-sm text-gray-600">
                {unit} - {currentTeam.sport}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {viewMode === 'builder' && (
              <>
                <button
                  onClick={() => {
                    if (confirm('Are you sure you want to cancel? Your changes will be lost.')) {
                      onClose();
                    }
                  }}
                  className="btn btn-ghost btn-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveFormation}
                  disabled={isSaving || !formationName.trim()}
                  className="btn btn-primary btn-sm"
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </>
            )}
            {savedFormationId && viewMode !== 'builder' && (
              <button
                onClick={handleDeleteFormation}
                className="btn btn-ghost btn-sm text-red-600 hover:text-red-700 hover:bg-red-50"
                title="Delete formation"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={onClose}
              className="btn btn-ghost btn-sm btn-circle"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {viewMode === 'select' && (
            <div className="p-6">
              <div className="max-w-6xl mx-auto space-y-6">
                {/* Option: Create Custom Formation */}
                <div className="text-center mb-8">
                  <button
                    onClick={handleStartCustomFormation}
                    className="btn btn-outline btn-lg"
                  >
                    Create Custom Formation from Scratch
                  </button>
                  <p className="text-sm text-gray-600 mt-2">
                    Start with a blank field and design your own formation
                  </p>
                </div>

                {/* Divider */}
                <div className="flex items-center gap-4 my-8">
                  <div className="flex-1 border-t"></div>
                  <span className="text-gray-500 font-medium">OR</span>
                  <div className="flex-1 border-t"></div>
                </div>

                {/* Option: Use Sample Formation */}
                <div>
                  <SampleFormations
                    sport={currentTeam.sport as Sport}
                    unit={unit}
                    onSelectFormation={handleSelectSampleFormation}
                    onUseAsTemplate={handleUseAsTemplate}
                  />
                </div>
              </div>
            </div>
          )}

          {viewMode === 'builder' && (
            <div className="flex flex-col h-full">
              {/* Formation Name/Description Section - Right below header */}
              <div className="flex-shrink-0 border-b bg-white p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Formation Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formationName}
                      onChange={(e) => {
                        setFormationName(e.target.value);
                        setValidationError(null); // Clear error when user types
                      }}
                      placeholder="Enter formation name"
                      className={`input input-bordered w-full ${validationError && !formationName.trim() ? 'input-error' : ''}`}
                    />
                    {validationError && !formationName.trim() && (
                      <p className="text-xs text-red-600 mt-1">{validationError}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description (optional)
                    </label>
                    <textarea
                      value={formationDescription}
                      onChange={(e) => setFormationDescription(e.target.value)}
                      placeholder="Enter a description for this formation"
                      className="textarea textarea-bordered w-full"
                      rows={2}
                    />
                  </div>
                </div>
                {validationError && formationName.trim() && (
                  <div className="alert alert-error py-2 px-3 mt-3">
                    <p className="text-xs">{validationError}</p>
                  </div>
                )}
              </div>

              {/* Visual Builder Section - Scrollable */}
              <div className="flex-1 overflow-y-auto min-h-0">
                <VisualFormationBuilder
                  sport={currentTeam.sport as Sport}
                  unit={unit}
                  initialPositions={currentPositions}
                  formationName={formationName}
                  onSave={(positions) => {
                    // Ensure we're using the latest positions with all updates
                    console.log('[FormationBuilderManager] Syncing positions from VisualFormationBuilder:', positions.map(p => ({ id: p.id, label: p.label, positionGroupKey: p.positionGroupKey })));
                    setCurrentPositions(positions);
                    latestPositionsRef.current = positions; // Update ref immediately
                  }}
                  onCancel={() => {
                    if (confirm('Are you sure you want to cancel? Your changes will be lost.')) {
                      onClose();
                    }
                  }}
                />
              </div>
            </div>
          )}

          {viewMode === 'mapping' && savedFormationId && (
            <div className="p-6">
              <div className="max-w-4xl mx-auto">
                <PositionMappingEditor
                  teamId={currentTeam.id}
                  sport={currentTeam.sport as Sport}
                  unit={unit}
                  formationId={savedFormationId}
                  formationName={formationName}
                  positions={currentPositions}
                  onSave={async () => {
                    // Refresh formations list before closing
                    if (onFormationCreated && savedFormationId) {
                      onFormationCreated(savedFormationId);
                    }
                    onClose();
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormationBuilderManager;

